// Email-ready reply text generator (PRD §7 Feature 5).
//
// Produces a plain-text body the editor copies into Gmail/Outlook. The body is
// structured so the chief editor can scan it quickly: decision first, then
// reasoning, then concrete next steps (reviewers or revisions).

import { DECISION_LABELS, type ExportInput } from './types';

export interface EmailExport {
  subject: string;
  body: string;
}

function decisionPhrase(d: ExportInput['draft']['decision']): string {
  if (!d) return 'Recommendation pending';
  return DECISION_LABELS[d];
}

function topMissingItems(input: ExportInput, limit = 5): string[] {
  const meth = input.pattern.methodology;
  if (!meth) return [];
  return meth.items
    .filter((i) => i.status === 'not_located' && i.severityIfMissing === 'red')
    .slice(0, limit)
    .map((i) => `  - ${i.itemId}: ${i.itemText}`);
}

function topIntegrityFindings(input: ExportInput, limit = 5): string[] {
  return input.pattern.findings
    .filter((f) => f.severity === 'red' || f.severity === 'amber')
    .filter((f) => f.category !== 'methodology')
    .slice(0, limit)
    .map((f) => `  - [${f.severity}] ${f.findingText}`);
}

/**
 * Crossref reference-audit findings for the email body. These live in
 * `input.referenceAudit` (not `pattern.findings`), so without this they were
 * visible on screen and in the PDF/JSON but SILENTLY ABSENT from the email —
 * the artifact the editor pastes to the chief editor. A dropped "Reference N
 * RETRACTED" is the single most reputationally serious omission, so retracted
 * entries are listed first. Severity tags mirror the on-screen integrity card.
 */
function referenceAuditLines(input: ExportInput, limit = 12): string[] {
  const ra = input.referenceAudit;
  if (!ra || ra.entries.length === 0) return [];
  const out: Array<{ sev: 'red' | 'amber'; text: string }> = [];
  for (const e of ra.entries) {
    const label = e.referenceNumber !== null ? `Reference ${e.referenceNumber}` : 'Reference';
    const o = e.outcome;
    if (o.status === 'retracted') {
      const dois = o.work.retractionDois.join(', ');
      out.push({
        sev: 'red',
        text: `${label}: cited as active but Crossref reports a RETRACTION (notice DOI ${dois || 'unknown'}).`,
      });
    } else if (o.status === 'mismatch') {
      out.push({ sev: 'amber', text: `${label}: metadata mismatch — ${o.mismatches.join('; ')}.` });
    } else if (o.status === 'unresolved' && o.reason === 'doi_not_found') {
      out.push({ sev: 'amber', text: `${label}: DOI ${e.doi ?? ''} does not resolve on Crossref.` });
    }
  }
  out.sort((a, b) => (a.sev === b.sev ? 0 : a.sev === 'red' ? -1 : 1));
  const lines = out.slice(0, limit).map((x) => `  - [${x.sev}] ${x.text}`);
  if (out.length > limit) lines.push(`  - …and ${out.length - limit} more`);
  return lines;
}

function reviewerLines(input: ExportInput): string[] {
  const selectedKeys = new Set(input.draft.selectedReviewerKeys);
  const fromApi = input.reviewers?.candidates.filter((c) => selectedKeys.has(c.key)) ?? [];
  const all = [...fromApi, ...input.draft.manualReviewers];
  if (all.length === 0) return [];
  return all.map((c) => {
    const parts = [c.name];
    if (c.affiliation) parts.push(c.affiliation);
    if (c.orcid) parts.push(`ORCID ${c.orcid}`);
    if (c.email) parts.push(c.email);
    return `  - ${parts.join(' · ')}`;
  });
}

export function generateEmail(input: ExportInput): EmailExport {
  const title = input.manuscript.title || input.fileName;
  const studyType = input.pattern.classifier.label;
  const guideline = input.pattern.methodology?.guidelineId ?? 'no formal guideline';

  const subject = `Skim recommendation: ${decisionPhrase(input.draft.decision)} — ${title.slice(0, 80)}`;

  const lines: string[] = [];
  lines.push(`Manuscript: ${title}`);
  lines.push(`Detected study type: ${studyType}${guideline !== 'no formal guideline' ? ` (${guideline})` : ''}`);
  lines.push('');
  lines.push(`Recommendation: ${decisionPhrase(input.draft.decision)}`);
  lines.push('');

  if (input.draft.rationale.trim()) {
    lines.push('Rationale:');
    lines.push(input.draft.rationale.trim());
    lines.push('');
  }

  const missing = topMissingItems(input);
  if (missing.length > 0) {
    lines.push('High-severity methodology items not located by the pattern engine:');
    lines.push(...missing);
    lines.push('');
  }

  const integrity = topIntegrityFindings(input);
  if (integrity.length > 0) {
    lines.push('Integrity flags:');
    lines.push(...integrity);
    lines.push('');
  }

  const refAudit = referenceAuditLines(input);
  if (refAudit.length > 0) {
    lines.push('Reference audit (Crossref):');
    lines.push(...refAudit);
    lines.push('');
  }

  const reviewers = reviewerLines(input);
  if (reviewers.length > 0) {
    lines.push('Suggested reviewers:');
    lines.push(...reviewers);
    lines.push('');
  }

  if (input.draft.editorNotes.trim()) {
    lines.push('Editor notes:');
    lines.push(input.draft.editorNotes.trim());
    lines.push('');
  }

  lines.push('---');
  lines.push(
    `Generated ${input.generatedAt} by ManuscriptSkim. Advisory triage only — the editor confirms every finding and the decision.`,
  );

  return { subject, body: lines.join('\n') };
}

/** Build a mailto: URI for short bodies. Returns null if length exceeds 1900 chars. */
export function buildMailtoUri(email: EmailExport): string | null {
  const url =
    `mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;
  return url.length <= 1900 ? url : null;
}
